import { useEffect, useRef, type CSSProperties } from 'react';
import clsx from 'clsx';

export interface ConfettiProps {
  /**
   * Trigger — increment this value (or toggle from false → true) to fire a burst.
   * Re-firing requires a new value, so pass a counter/timestamp for repeat bursts.
   */
  trigger: unknown;
  /** How many particles per burst. Default 80. */
  count?: number;
  /** Origin x fraction (0 = left, 1 = right). Default 0.5. */
  originX?: number;
  /** Origin y fraction (0 = top, 1 = bottom). Default 0.35. */
  originY?: number;
  /** Spread angle in degrees. Default 80. */
  spread?: number;
  /** Particle palette override. Default = brand palette + gold + accent. */
  colors?: string[];
  /** Pin the canvas over a specific element (e.g. a node) — defaults to full viewport. */
  className?: string;
  style?: CSSProperties;
}

const DEFAULT_COLORS = ['#58cc02', '#e8a317', '#e26124', '#d9582e', '#8cdd2e', '#1cb0f6', '#ffffff'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  size: number;
  alpha: number;
  shape: 'rect' | 'circle';
  /** Horizontal sinusoidal sway — phase offset (rad). */
  swayPhase: number;
  /** Horizontal sway frequency (rad/ms). */
  swayFreq: number;
  /** Horizontal sway amplitude (px, applied at draw time). */
  swayAmp: number;
  /** ScaleY flutter phase (rad) — rect particles only, fake-3D tumble. */
  flutterPhase: number;
  /** ScaleY flutter frequency (rad/ms). */
  flutterFreq: number;
  /** Per-particle terminal fall speed (px/frame) — light/heavy pieces. */
  terminalVy: number;
}

/**
 * Confetti — canvas-based particle burst. Fires once per `trigger` value change.
 * No external dep, uses our brand palette, respects prefers-reduced-motion.
 *
 * Canvas is fixed, pointer-events none, and sits on top (z-index just
 * below toasts). For a scoped burst (e.g. from a specific node), wrap
 * the component in a positioned container and pass `className`.
 */
export function Confetti({
  trigger,
  count = 80,
  originX = 0.5,
  originY = 0.35,
  spread = 80,
  colors = DEFAULT_COLORS,
  className,
  style,
}: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const prevTrigger = useRef<unknown>(undefined);

  useEffect(() => {
    if (prevTrigger.current === undefined) {
      prevTrigger.current = trigger;
      return;
    }
    if (prevTrigger.current === trigger) return;
    prevTrigger.current = trigger;
    fireBurst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  function fireBurst() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduceMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const particles: Particle[] = [];
    const cx = width * originX;
    const cy = height * originY;
    const spreadRad = (spread * Math.PI) / 180;
    const gravity = 0.22;
    const friction = 0.995;

    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * spreadRad;
      const speed = 6 + Math.random() * 5;
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)] ?? '#58cc02',
        size: 6 + Math.random() * 6,
        alpha: 1,
        shape: Math.random() < 0.3 ? 'circle' : 'rect',
        swayPhase: Math.random() * Math.PI * 2,
        swayFreq: 0.004 + Math.random() * 0.004,
        swayAmp: 5 + Math.random() * 9,
        flutterPhase: Math.random() * Math.PI * 2,
        flutterFreq: 0.008 + Math.random() * 0.008,
        terminalVy: 7 + Math.random() * 3.5,
      });
    }

    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);
      let alive = 0;
      for (const p of particles) {
        p.vy += gravity;
        p.vx *= friction;
        p.vy *= friction;
        // Per-particle terminal velocity — light pieces drift, heavy ones drop.
        if (p.vy > p.terminalVy) p.vy = p.terminalVy;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        // fade after 70% of 2.5s lifespan
        p.alpha = elapsed < 1800 ? 1 : Math.max(0, 1 - (elapsed - 1800) / 700);
        if (p.alpha <= 0 || p.y > height + 20) continue;
        alive++;
        // Horizontal sinusoidal sway (draw-time offset — no drift accumulation).
        const swayX = Math.sin(elapsed * p.swayFreq + p.swayPhase) * p.swayAmp;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x + swayX, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') {
          // ScaleY flutter — the strip appears to tumble over its long axis.
          const flutter = Math.sin(elapsed * p.flutterFreq + p.flutterPhase);
          ctx.scale(1, 0.25 + Math.abs(flutter) * 0.75);
          ctx.fillRect(-p.size / 2, -p.size / 3, p.size, p.size / 1.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      if (alive > 0 && elapsed < 3000) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }

  return (
    <canvas
      ref={canvasRef}
      className={clsx('fluent-confetti-canvas', className)}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 'var(--z-toast)' as unknown as number,
        ...style,
      }}
      aria-hidden="true"
    />
  );
}
